import { HelpCenterKnowledge, SgwtHelpCenterHTMLElement } from '@sgwt/sgwt-widgets-react';
import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

/**
 * Expose some Help Center TypeScript private methods until we have a better solution and enrich addEventListener
 * @see https://sgithub.fr.world.socgen/SG-Web-Toolkit/sgwt-widgets/issues/925
 */
export interface HelpCenter extends SgwtHelpCenterHTMLElement {
  expanded: boolean;
  retrieveKnowledge(url: string): void;
  getKnowledgeUrlFromApplicationId(): string;
  addEventListener<K extends HelpCenterEventNames>(
    event: K,
    handler: (this: Element, ev: HelpCenterEventByEventName<K>) => void,
    options?: boolean | AddEventListenerOptions,
  ): void;
  removeEventListener<K extends HelpCenterEventNames>(
    event: K,
    handler: (this: Element, ev: HelpCenterEventByEventName<K>) => void,
    options?: boolean | AddEventListenerOptions,
  ): void;
  changeExpandableState(): void;
}

interface KnowledgeEvent {
  detail: { knowledge: HelpCenterKnowledge };
}

type HelpCenterSpecificEventNames = 'sgwt-help-center--update-knowledge' | 'sgwt-help-center--update-language';
type HelpCenterEventNames = keyof HTMLElementEventMap | HelpCenterSpecificEventNames;
type HelpCenterEventByEventName<K> = K extends keyof HTMLElementEventMap
  ? HTMLElementEventMap[K]
  : K extends 'sgwt-help-center--update-knowledge'
    ? KnowledgeEvent
    : never;

let storedKnowledge: HelpCenterKnowledge | undefined;
let storedUrl: string | undefined;

const retrieveHelpCenterKnowledge = (helpCenter: HelpCenter | null) => {
  if (!helpCenter || !helpCenter.getKnowledgeUrlFromApplicationId || !helpCenter.retrieveKnowledge || storedKnowledge) {
    storedUrl = undefined;
    return;
  }
  const url = helpCenter.getKnowledgeUrlFromApplicationId();

  const isTheSameUrl = storedUrl === url;

  if (isTheSameUrl) {
    return;
  }

  storedUrl = url;
  helpCenter.retrieveKnowledge(url);
};

const useHelpCenterKnowledge = (helpCenterRef: RefObject<HelpCenter | null>) => {
  const [knowledge, setKnowledge] = useState(storedKnowledge);

  useEffect(() => {
    retrieveHelpCenterKnowledge(helpCenterRef.current);
  }, [helpCenterRef]);

  useEffect(() => {
    const helpCenterElem = helpCenterRef.current;
    if (!helpCenterElem || knowledge) {
      return;
    }

    const handler = ({ detail }: KnowledgeEvent) => {
      storedKnowledge = detail.knowledge;
      setKnowledge(storedKnowledge);
    };

    helpCenterElem.addEventListener('sgwt-help-center--update-knowledge', handler);
    return () => {
      helpCenterElem.removeEventListener('sgwt-help-center--update-knowledge', handler);
    };
  }, [helpCenterRef, knowledge]);

  return knowledge;
};

const useHelpCenterRef = () => {
  const helpCenterRef = useRef<HelpCenter | null>(null);
  useEffect(() => {
    helpCenterRef.current = document.querySelector<HelpCenter>('sgwt-help-center');
  }, []);
  return helpCenterRef;
};

export function useHelpCenter() {
  const helpCenterRef = useHelpCenterRef();
  const knowledge = useHelpCenterKnowledge(helpCenterRef);
  const getTopicById = useCallback((id: string) => knowledge?.topics?.find(topic => topic.id === id), [knowledge]);

  const openTopicId = useCallback(
    (id: string): void => {
      helpCenterRef.current?.close();
      helpCenterRef.current?.topic(id);
    },
    [helpCenterRef],
  );

  const openCategoryId = useCallback(
    (id: string): void => {
      helpCenterRef.current?.close();
      helpCenterRef.current?.category(id);
    },
    [helpCenterRef],
  );

  const isHelpCenterReadyAndExpanded = useCallback(() => {
    const helpCenterElem = helpCenterRef.current;
    if (!helpCenterElem) {
      return false;
    }
    if (!helpCenterElem.expanded) {
      helpCenterElem.changeExpandableState();
    }
    return true;
  }, [helpCenterRef]);

  return { knowledge, helpCenterRef, getTopicById, openTopicId, openCategoryId, isHelpCenterReadyAndExpanded };
}
